/**
 * Tide display types and formatters, with no dependencies.
 *
 * Client components (the tide chart, the insights panels and the coastal
 * briefing rendered by the "use client" coastal page) import from here, never
 * from `tides.ts`, so the harmonic predictor in `tide-harmonics.ts` stays on
 * the server and out of the browser bundle. `tide-client-bundle.test.ts`
 * fails if a client entry point reaches the predictor.
 */

export type TidePoint = {
  hour: number;
  heightM: number;
  state: "rising" | "falling" | "high" | "low";
};

/**
 * What a displayed tide height is measured from:
 * - "msl": metres relative to mean sea level (negative below it), from the
 *   harmonic predictor;
 * - "low-water": metres above the lunar model's own low water (the legacy
 *   planner input, and detail payloads cached before 27 September 2026);
 * - "none": the location-blind lunar fallback. Its heights only shape the
 *   curve and are never shown as numbers, and its times are estimates from
 *   the moon's age alone.
 */
export type TideDatum = "msl" | "low-water" | "none";

/**
 * Displayed tide curve for one local day.
 *
 * `hour` values of the high and low waters are local decimal hours (for
 * example 13.4667 for 13:28; harmonic times are whole minutes, the lunar
 * fallback's tenths of an hour); format them with `formatTideTime`.
 * `isApproximate` is true when the curve comes from the lunar fallback or
 * from constituents whose source gauge is unverified or more than
 * TIDE_REFERENCE_MAX_DISTANCE_KM away.
 */
export type TideData = {
  points: TidePoint[];
  highTides: { hour: number; heightM: number }[];
  lowTides: { hour: number; heightM: number }[];
  rangeM: number;
  isApproximate: boolean;
  /**
   * Absent on detail payloads cached before 27 September 2026 (their heights
   * are the old 0 to 3 m positions); readers treat a missing datum as
   * "low-water".
   */
  datum?: TideDatum;
};

/** "HH:MM" for a local decimal hour; minutes never read 60 and 24:00 wraps to 00:00. */
export function formatTideTime(decimalHour: number): string {
  const totalMinutes = Math.round(decimalHour * 60);
  const h = ((Math.floor(totalMinutes / 60) % 24) + 24) % 24;
  const m = ((totalMinutes % 60) + 60) % 60;
  const hh = h.toString().padStart(2, "0");
  const mm = m.toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * Height for display: signed relative to mean sea level ("+1.2 m", "-0.8 m")
 * or unsigned above low water ("2.5 m"). There is no form for the "none"
 * datum because the lunar fallback's heights are never shown.
 */
export function formatTideHeight(heightM: number, datum: Exclude<TideDatum, "none">): string {
  const value = Math.abs(heightM) < 0.05 ? 0 : heightM;
  if (datum === "low-water") return `${value.toFixed(1)} m`;
  return `${value > 0 ? "+" : value < 0 ? "-" : ""}${Math.abs(value).toFixed(1)} m`;
}

export function tideStateLabel(point: TidePoint): string | null {
  if (point.state === "high") return "High";
  if (point.state === "low") return "Low";
  return null;
}
